/**
 * storyEntry.js — "this tab is trying to reach a highlights story" marker.
 *
 * Two problems share one answer:
 *
 *   1. A phone user who lands anywhere on the desktop app is auto-sent to /m
 *      (see MobileRedirect). That is right for someone who typed the domain, but
 *      wrong for someone a colleague sent a link to ONE story — they should end
 *      up on that story, not the mobile map.
 *   2. A GATED story bounces a logged-out visitor to the login screen, and they
 *      should land back on the story afterwards rather than the usual homepage.
 *
 * Both need a destination that outlives a navigation to "/", so the entry path
 * is parked in sessionStorage (per-tab, cleared when the tab closes).
 *
 * Writers: MobileRedirect (marks a phone tab that OPENED on a story, so the /m
 * diversion stands down) and StoryGate (marks the story it is bouncing someone
 * away from). Reader: SignIn, which finishes the journey after login.
 */
import { isStoryPath } from "../highlights/stories";

const STORY_ENTRY_KEY = "storyEntryPath";

export { isStoryPath };

/**
 * Called once per mount with the URL the tab opened on. If that URL is a story,
 * mark the tab; either way, report whether this tab is a story tab — a mark set
 * on an earlier page load of the same journey still counts.
 */
export function initStoryEntry(fullPath) {
  if (isStoryPath(fullPath)) {
    try { sessionStorage.setItem(STORY_ENTRY_KEY, fullPath); } catch { /* ignore */ }
    return true;
  }
  try { return isStoryPath(sessionStorage.getItem(STORY_ENTRY_KEY)); } catch { return false; }
}

/** Record where to return to after login. */
export function rememberStoryEntry(path) {
  if (!isStoryPath(path)) return;
  try { sessionStorage.setItem(STORY_ENTRY_KEY, path); } catch { /* ignore */ }
}

/** Read + consume the pending story destination (null when there isn't one). */
export function takeStoryEntry() {
  let stored = null;
  try {
    stored = sessionStorage.getItem(STORY_ENTRY_KEY);
    sessionStorage.removeItem(STORY_ENTRY_KEY);
  } catch { /* ignore */ }
  return isStoryPath(stored) ? stored : null;
}

/** Drop the mark — the user has moved on (e.g. exited the reel into /m). */
export function clearStoryEntry() {
  try { sessionStorage.removeItem(STORY_ENTRY_KEY); } catch { /* ignore */ }
}
